import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, ViewChild, TemplateRef, ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, FormControl, AbstractControl, Validators } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/observable/of';
import { ToasterService } from 'angular2-toaster';
import swal from 'sweetalert2';
import * as _ from 'lodash';
import * as XLSX from 'xlsx';
import * as moment from 'moment';
import { Store } from '@ngrx/store';
import * as fromCommonStore from '../../../store';
import * as fromRootStore from '../../../reducers';
import { HamodelModel } from '../../../model/index';
import { HamodelService, CommonService } from '../../../services/index';

@Component({
    selector: 'followup-change-tab',
    templateUrl: './followupChange.component.html',
})
export class FollowupChangeComponent implements OnInit, OnDestroy, OnChanges {
    @Input('tabTitle') title: string;
    @Input() active = false;
    @Input() isCloseable = false;
    @Input('params') params: string;

    public mode: string;

    // Form Controls
    clientNoCtrl: FormControl;
    clientNameCtrl: FormControl;
    fromFollowupCtrl: FormControl;
    toFollowupCtrl: FormControl;
    assignToCtrl: FormControl;

    // Observables for dropdowns
    filteredToFollowup: Observable<any[]>;
    filteredAssignTo: Observable<any[]>;
    filteredClientList: Observable<any[]>;
    filteredFollowupStatusList: Observable<any[]>;
    filteredAssignToList: Observable<any[]>;

    // Data arrays
    followupList: any[] = [];
    assignToList: any[] = [];
    clientList: any[] = [];

    // Form data object
    followupData = {};  // Keep as object for form data binding

    // Loading states
    isLoading: boolean = false;
    isLoadMask: Promise<any>;

    // File upload
    selectedFile: File = null;
    uploadProgress: number = 0;

    // Excel data
    excelData: any[] = [];
    excelColumns = [];

    // Excel toggle
    showExcelUpload: boolean = false;

    // Selected user for display
    selectedAssignToUser: any = null;

    constructor(
        public store: Store<fromRootStore.State>,
        public toasterService: ToasterService,
        private commonService: CommonService,
        private http: HttpClient,
        private cdr: ChangeDetectorRef
    ) {
        // Initialize data objects like other data correction components
        this.followupData = {};  // Keep as object for form data binding
        this.excelData = [];
        this.followupList = [];
        this.assignToList = [];

        // Initialize form controls
        this.clientNoCtrl = new FormControl('', [Validators.required]);
        this.clientNameCtrl = new FormControl({value: '', disabled: true});
        this.fromFollowupCtrl = new FormControl({value: '', disabled: true});
        this.toFollowupCtrl = new FormControl('', [Validators.required]);
        this.assignToCtrl = new FormControl('', [Validators.required]);

        // Setup autocomplete
        this.filteredToFollowup = this.toFollowupCtrl.valueChanges
            .startWith(null)
            .map(name => name ? this.filterFollowup(name) : this.followupList.slice());

        this.filteredAssignTo = this.assignToCtrl.valueChanges
            .startWith(null)
            .map(name => name ? this.filterAssignTo(name) : this.assignToList.slice());

        this.filteredClientList = this.clientNoCtrl.valueChanges
            .startWith('')
            .debounceTime(300)
            .distinctUntilChanged()
            .switchMap(search => {
                if (search && typeof search === 'string' && search.length >= 2) {
                    console.log('Searching for:', search);
                    return this.searchClients(search);
                }
                return Observable.of([]);
            });

        this.filteredFollowupStatusList = this.toFollowupCtrl.valueChanges
            .startWith('')
            .debounceTime(300)
            .distinctUntilChanged()
            .switchMap(search => {
                if (search && typeof search === 'string' && search.length >= 1) {
                    console.log('Searching followup status for:', search);
                    return this.searchFollowupStatus(search);
                }
                return Observable.of([]);
            });

        this.filteredAssignToList = this.assignToCtrl.valueChanges
            .startWith('')
            .debounceTime(300)
            .distinctUntilChanged()
            .switchMap(search => {
                if (search && typeof search === 'string' && search.length >= 1) {
                    console.log('Searching assign to users for:', search);
                    return this.searchAssignToUsers(search);
                }
                return Observable.of(this.assignToList || []);
            });
    }

    ngOnInit(): void {
        this.resetComponentState();
        this.loadAssignToList();
        this.setupExcelColumns();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Reset component state when tab becomes active
        if (changes.active && changes.active.currentValue === true) {
            this.resetComponentState();
        }
    }

    private resetComponentState(): void {
        // Reset component state like other data correction components
        this.showExcelUpload = false;
        this.selectedFile = null;
        this.excelData = [];
        this.followupData = {};
        this.selectedAssignToUser = null;

        // Reset form controls
        if (this.clientNoCtrl) {
            this.clientNoCtrl.reset();
            this.clientNameCtrl.reset();
            this.fromFollowupCtrl.reset();
            this.toFollowupCtrl.reset();
            this.assignToCtrl.reset();
        }
    }


    loadAssignToList(): void {
        // Initial load - will be replaced by dynamic loading based on followup status
        this.assignToList = [];
    }

    loadFollowers(followupStatusId: any): void {
        console.log('Loading followers for status ID:', followupStatusId);
        const query = { fol_id: followupStatusId };
        this.commonService.getUserList(query)
            .then((res: any) => {
                console.log('Followers response:', res);
                if (res.success && res.data) {
                    this.assignToList = res.data;
                } else {
                    this.assignToList = [];
                    this.showError('No users found for selected followup status');
                }
                this.cdr.detectChanges();
            })
            .catch((err) => {
                console.error('Error loading followers:', err);
                this.assignToList = [];
                this.showError('Error loading assign to list');
            });
    }

    setupExcelColumns(): void {
        this.excelColumns = [
            { name: 'Client No', prop: 'client_no', sortable: false, cellClass: 'cell-left' },
            { name: 'From Followup', prop: 'from_followup', sortable: false, cellClass: 'cell-left' }
        ];
    }

    filterFollowup(name: string) {
        name = _.escapeRegExp(name);
        return this.followupList.filter(item => (new RegExp(name, 'gi').test(item.status_name)));
    }

    filterAssignTo(name: string) {
        name = _.escapeRegExp(name);
        return this.assignToList.filter(item => (new RegExp(name, 'gi').test(item.fullname)));
    }

    searchClients(search: string): Observable<any[]> {
        if (!search || search.length < 2) {
            return Observable.of([]);
        }
        console.log('Making API call for search:', search);
        return Observable.fromPromise(
            this.commonService.searchClientByNo({ search: search })
                .then((res: any) => {
                    console.log('API response:', res);
                    return res.success ? res.data : [];
                })
                .catch((err) => {
                    console.error('API error:', err);
                    return [];
                })
        );
    }

    searchFollowupStatus(search: string): Observable<any[]> {
        if (!search || search.length < 1) {
            return Observable.of([]);
        }
        console.log('Making followup status API call for search:', search);
        return Observable.fromPromise(
            this.commonService.searchFollowupStatus({ search: search })
                .then((res: any) => {
                    console.log('Followup status API response:', res);
                    return res.success ? res.data : [];
                })
                .catch((err) => {
                    console.error('Followup status API error:', err);
                    return [];
                })
        );
    }

    searchAssignToUsers(search: string): Observable<any[]> {
        if (!search || search.length < 1) {
            return Observable.of(this.assignToList || []);
        }
        console.log('Filtering assign to users for search:', search);
        const filteredUsers = this.assignToList.filter(user => {
            const fullname = user.fullname || user.empname || user.name || '';
            return fullname.toLowerCase().includes(search.toLowerCase());
        });
        return Observable.of(filteredUsers);
    }

    displayFollowup(statusId: any) {
        if (this.followupList && this.followupList.length > 0) {
            let item = _.find(this.followupList, { 'poc_status_id': statusId });
            return item ? item.status_name : '';
        }
        return '';
    }

    displayAssignTo(userId: any) {
        if (this.assignToList && this.assignToList.length > 0) {
            let item = _.find(this.assignToList, { 'user_id': userId });
            return item ? item.fullname : '';
        }
        return '';
    }

    displayClient(client: any): string {
        return client ? client.client_no : '';
    }

    displayFollowupStatus(status: any): string {
        return status ? status.status_name : '';
    }

    displayAssignToUser(user: any): string {
        return user ? (user.fullname || user.empname || user.name) : '';
    }

    onFollowupStatusSelect(status: any): void {
        if (status && typeof status === 'object' && status.poc_status_id) {
            console.log('Followup status selected:', status);
            this.followupData['to_followup'] = status.poc_status_id;

            // Clear current assign to selection
            this.followupData['assign_to'] = '';
            this.selectedAssignToUser = null;
            this.assignToCtrl.setValue(null);

            // Load followers for the selected followup status
            this.loadFollowers(status.poc_status_id);

            this.cdr.detectChanges();
        }
    }

    onAssignToSelect(selectedUser: any): void {
        if (selectedUser && typeof selectedUser === 'object') {
            console.log('User selected:', selectedUser);
            this.selectedAssignToUser = selectedUser;
            this.followupData['assign_to'] = selectedUser.user_id || selectedUser.id || selectedUser.emp_id;
            this.cdr.detectChanges();
        }
    }


    onClientSelect(client: any): void {
        console.log('onClientSelect called with:', client);
        if (client && typeof client === 'object' && client.client_no) {
            console.log('Client selected:', client);

            // Set the form data
            this.followupData['client_no'] = client.client_no;
            this.followupData['client_name'] = client.client_name;
            this.followupData['from_followup'] = client.followup_status || '';

            // Update the form controls
            this.clientNameCtrl.setValue(client.client_name);
            this.fromFollowupCtrl.setValue(client.followup_status || '');

            // Trigger change detection
            this.cdr.detectChanges();

            console.log('Updated followupData:', this.followupData);
        } else {
            console.log('Invalid client selection:', client);
        }
    }

    onClientNoChange(): void {
        const clientInput = this.clientNoCtrl.value;
        if (!clientInput) {
            this.followupData['client_name'] = '';
            this.followupData['from_followup'] = '';
            this.clientNameCtrl.setValue('');
            this.fromFollowupCtrl.setValue('');
            return;
        }

        if (typeof clientInput === 'string') {
            this.followupData['client_no'] = clientInput;
            // Client details will be populated when selected from autocomplete
        }
    }




    onFileSelected(event: any): void {
        let workBook = null;
        let jsonData = null;
        const reader = new FileReader();
        if ((event.target.files.length == 0) || (event.target.files.length < 0)) {
            return;
        }
        const file = event.target.files[0];
        this.selectedFile = file;

        if (!this.selectedFile.name.endsWith('.xlsx') && !this.selectedFile.name.endsWith('.xls')) {
            swal('WARNING', 'Please select a valid Excel file (.xlsx or .xls)');
            this.selectedFile = null;
            return;
        }

        reader.onload = (event) => {
            const data = reader.result;
            workBook = XLSX.read(data, { type: 'binary', cellDates: true });
            jsonData = workBook.SheetNames.reduce((initial, name) => {
                const sheet = workBook.Sheets[name];
                initial[name] = XLSX.utils.sheet_to_json(sheet);
                return initial;
            }, {});

            if (Object.keys(jsonData).length == 1) {
                this.excelData = jsonData.Sheet1 || [];
                console.log('Excel data loaded:', this.excelData);
            }
        };
        reader.readAsBinaryString(file);
    }


    processExcelFollowupChange(): void {
        if (!this.excelData || this.excelData.length === 0) {
            swal('WARNING', 'No Excel data to process');
            return;
        }

        const to_followup_status = this.toFollowupCtrl.value;
        const assign_to_user = this.assignToCtrl.value;

        if (!to_followup_status || !assign_to_user) {
            swal('WARNING', 'Please select To Followup and Assign To values');
            return;
        }

        const confirmMessage = `Are you sure you want to change followup for ${this.excelData.length} records to ${to_followup_status.status_name}?`;

        swal({
            title: 'Confirm Bulk Followup Change',
            text: confirmMessage,
            showCancelButton: true,
            confirmButtonText: 'Yes, Process All!',
            cancelButtonText: 'Cancel'
        }).then(() => {
            this.processBulkFollowupChange();
        }, () => {});
    }

    private processBulkFollowupChange(): void {
        const to_followup_status = this.toFollowupCtrl.value;
        const assign_to_user = this.assignToCtrl.value;

        // Prepare bulk data for backend processing
        const bulkData = {
            excel_data: this.excelData,
            to_followup: to_followup_status.status_name,
            to_followup_id: to_followup_status.poc_status_id,
            assign_to: assign_to_user.fullname || assign_to_user.empname || assign_to_user.name,
            assign_to_id: assign_to_user.user_id || assign_to_user.id || assign_to_user.emp_id || assign_to_user.userid
        };

        console.log('Bulk data being sent:', bulkData);

        this.isLoading = true;
        this.commonService.processBulkFollowupChange(bulkData)
            .then((res: any) => {
                this.isLoading = false;
                if (res.success) {
                    this.resetForm();
                    swal('SUCCESS', res.message, 'success');
                } else {
                    this.showError(res.message);
                }
            })
            .catch(() => {
                this.isLoading = false;
                this.showError('Error processing bulk followup change');
            });
    }


    downloadSampleExcel(): void {
        const ws = XLSX.utils.aoa_to_sheet([
            ['CLIENT NO', 'FROM FOLLOWUP'],
            ['', '']
        ]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        const fileName = 'Followup_Change_Sample_' + moment().format('DD-MM-YYYY_HH-mm') + '.xlsx';
        XLSX.writeFile(wb, fileName);
        this.showSuccess('Sample Excel downloaded successfully');
    }

    onExcelToggleChange(event: any): void {
        this.showExcelUpload = event.target.checked;
        if (!this.showExcelUpload) {
            this.selectedFile = null;
            this.excelData = [];
        }
    }

    processFollowupChange(): void {
        const client_no = this.followupData['client_no'];
        const from_followup = this.followupData['from_followup'];
        const to_followup_status = this.toFollowupCtrl.value;
        const assign_to_user = this.assignToCtrl.value;

        // Validate required fields with actual data values
        if (!client_no || !to_followup_status || !assign_to_user) {
            swal('WARNING', 'Please fill in all required fields');
            return;
        }

        console.log('assign_to_user object:', assign_to_user);
        console.log('to_followup_status object:', to_followup_status);

        let assign_to_id;
        if (typeof assign_to_user === 'object' && assign_to_user !== null) {
            assign_to_id = assign_to_user.user_id || assign_to_user.id || assign_to_user.emp_id || assign_to_user.userid;
        } else {
            assign_to_id = assign_to_user;
        }

        let to_followup_id;
        if (typeof to_followup_status === 'object' && to_followup_status !== null) {
            to_followup_id = to_followup_status.poc_status_id || to_followup_status.id;
        } else {
            to_followup_id = to_followup_status;
        }

        const data = {
            client_no: client_no,
            from_followup: from_followup,
            to_followup: to_followup_id,
            assign_to: assign_to_id
        };

        console.log('Final data being sent:', data);

        this.isLoading = true;
        this.commonService.processFollowupChange(data)
            .then((res: any) => {
                this.isLoading = false;
                if (res.success) {
                    this.resetForm();
                    swal('SUCCESS', res.message, 'success');
                } else {
                    this.showError(res.message);
                }
            })
            .catch((error) => {
                this.isLoading = false;
                console.error('Error processing followup change:', error);
                this.showError('Error processing followup change. Please try again.');
            });
    }

    resetForm(): void {
        this.resetComponentState();
    }

    showError(message: string): void {
        const toast = {
            type: 'error',
            title: 'ERROR',
            body: message
        };
        this.toasterService.pop(toast);
    }


    showSuccess(message: string): void {
        const toast = {
            type: 'success',
            title: 'SUCCESS',
            body: message
        };
        this.toasterService.pop(toast);
    }

    ngOnDestroy(): void {
        // Clean up component state like other data correction components
        this.showExcelUpload = false;
        this.selectedFile = null;
        this.excelData = [];
        this.followupData = {};
    }
}


// WEBPACK FOOTER //
// /Applications/MAMP/htdocs/crm-soundsgood/src/app/components/dataCorrection/followupChange/followupChange.component.ts